// QuickBooksService.js
//
// Client wrappers for QuickBooks Online Connect (no tokens on the client).

import { functions, httpsCallable } from 'firebaseConfig';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const callable = (name) => httpsCallable(functions, name);

function cleanString(value) {
  return String(value || '').trim();
}

export function emptyQboConnection() {
  return {
    connected: false,
    realmId: null,
    companyName: null,
    environment: 'sandbox',
    connectedAt: null,
    connectedByUserKey: null,
    defaultIncomeItemId: null,
    defaultArAccountId: null,
    depositAccountId: null,
    feeExpenseAccountId: null,
    taxCodeId: null,
    currencyLocked: null,
    mappingComplete: false,
  };
}

export function readPublicQuickBooks(base) {
  const raw = base?.quickbooks && typeof base.quickbooks === 'object'
    ? base.quickbooks
    : {};
  return {
    ...emptyQboConnection(),
    ...raw,
    connected: raw.connected === true,
    mappingComplete: raw.mappingComplete === true,
  };
}

export async function startQboConnect({ baseKey }) {
  const key = cleanString(baseKey);
  if (!key) throw new Error('baseKey is required');
  const fn = callable('startQboConnect');
  const { data } = await fn({
    ...buildTenantCallableFields(key),
  });
  return data || null;
}

export async function getQboConnection({ baseKey, includeCatalog = true }) {
  const key = cleanString(baseKey);
  if (!key) throw new Error('baseKey is required');
  const fn = callable('getQboConnection');
  const { data } = await fn({
    ...buildTenantCallableFields(key),
    includeCatalog,
  });
  return {
    connection: data?.connection || emptyQboConnection(),
    accounts: Array.isArray(data?.accounts) ? data.accounts : [],
    items: Array.isArray(data?.items) ? data.items : [],
  };
}

export async function saveQboMapping({
  baseKey,
  defaultIncomeItemId,
  defaultArAccountId,
  depositAccountId,
  feeExpenseAccountId = '',
  taxCodeId = '',
}) {
  const key = cleanString(baseKey);
  if (!key) throw new Error('baseKey is required');
  const fn = callable('saveQboMapping');
  const { data } = await fn({
    ...buildTenantCallableFields(key),
    defaultIncomeItemId,
    defaultArAccountId,
    depositAccountId,
    feeExpenseAccountId,
    taxCodeId,
  });
  return data?.connection || emptyQboConnection();
}

export async function disconnectQbo({ baseKey }) {
  const key = cleanString(baseKey);
  if (!key) throw new Error('baseKey is required');
  const fn = callable('disconnectQbo');
  const { data } = await fn({
    ...buildTenantCallableFields(key),
  });
  return data?.connection || emptyQboConnection();
}

export function readInvoiceQboSync(node) {
  const raw = node?.data_invoice?.qboSync;
  const bag = raw && typeof raw === 'object' ? raw : {};
  return {
    syncStatus: cleanString(bag.syncStatus) || '',
    syncError: cleanString(bag.syncError),
    syncSkippedReason: cleanString(bag.syncSkippedReason),
    lastSyncedAt: bag.lastSyncedAt || null,
  };
}

export async function syncQboInvoice({ baseKey, invoiceKey, reason = 'manual' }) {
  const key = cleanString(baseKey);
  const invoice = cleanString(invoiceKey);
  if (!key) throw new Error('baseKey is required');
  if (!invoice) throw new Error('invoiceKey is required');
  const fn = callable('syncQboInvoice');
  const { data } = await fn({
    ...buildTenantCallableFields(key),
    invoiceKey: invoice,
    reason,
  });
  return data || { status: 'error', reason: 'No response from QuickBooks sync.' };
}

/**
 * Best-effort enqueue after Issue / Log Payment / Void. Never throws.
 */
export async function enqueueInvoiceQboSync({
  baseKey,
  invoiceNode = null,
  invoiceKey = '',
  reason = 'manual',
}) {
  const key = cleanString(baseKey || invoiceNode?.baseKey);
  const invoice = cleanString(invoiceKey || invoiceNode?.key);
  if (!key || !invoice) return null;
  try {
    return await syncQboInvoice({ baseKey: key, invoiceKey: invoice, reason });
  } catch (error) {
    console.warn('[qbo] invoice sync failed', error);
    return {
      status: 'error',
      reason: error?.message || 'Could not sync to QuickBooks.',
    };
  }
}

export async function previewOpenQboInvoices({ baseKey, windowDays = 90 }) {
  const key = cleanString(baseKey);
  if (!key) throw new Error('baseKey is required');
  const fn = callable('syncOpenQboInvoices');
  const { data } = await fn({
    ...buildTenantCallableFields(key),
    preview: true,
    windowDays,
  });
  return data || { count: 0, invoices: [] };
}

export async function syncOpenQboInvoices({ baseKey, windowDays = 90 }) {
  const key = cleanString(baseKey);
  if (!key) throw new Error('baseKey is required');
  const fn = callable('syncOpenQboInvoices');
  const { data } = await fn({
    ...buildTenantCallableFields(key),
    preview: false,
    windowDays,
  });
  return data || { status: 'error', count: 0, results: [] };
}
